// IOSArenaAudioAdapter.ts - WebKit/iOS-specific audio platform adapter
import { IArenaAudioPlatformAdapter } from './IArenaAudioPlatformAdapter';

export class IOSArenaAudioAdapter implements IArenaAudioPlatformAdapter {
  public readonly platformName = 'ios';
  private prewarmedThisGesture: boolean = false;
  private isVoiceActive: boolean = false;

  public createAudioContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return null;

    let ctx: AudioContext | null = null;
    try {
      // iOS WebKit handles 48kHz cleanly without non-standard latency hints
      ctx = new AudioCtx({ sampleRate: 48000 });
    } catch {
      try {
        ctx = new AudioCtx();
      } catch {
        return null;
      }
    }

    this.configureSession(this.isVoiceActive);
    return ctx;
  }

  public prewarmAndUnlock(ctx: AudioContext, isVoiceActive?: boolean): void {
    if (typeof window === 'undefined') return;

    if (isVoiceActive !== undefined) {
      this.isVoiceActive = isVoiceActive;
    }

    // Fast-path: if audio context is already actively running and clock is ticking, skip heavy prewarming
    if (ctx.state === 'running' && this.prewarmedThisGesture && (ctx.currentTime || 0) > 0) return;

    this.configureSession(this.isVoiceActive);

    // 1. Resume AudioContext from suspended or interrupted state
    if (ctx.state === 'suspended' || (ctx.state as any) === 'interrupted') {
      ctx.resume().catch(() => {});
    }

    // 2. Prime WebKit CoreAudio clock via 1-sample silent WebAudio buffer without creating media player controls
    try {
      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
    } catch {}

    // NOTE: Do NOT create or play HTMLAudioElement (<audio>) tags.
    // Playing an HTMLAudioElement exposes iOS Media Player UI controls (Now Playing / Control Center scrubber).
    // Web Audio API buffer priming is fully sufficient to unlock CoreAudio without triggering media player UI.

    this.prewarmedThisGesture = true;
  }

  public onForeground(ctx: AudioContext | null, isVoiceActive: boolean): void {
    this.prewarmedThisGesture = false;
    this.isVoiceActive = isVoiceActive;
    this.configureSession(isVoiceActive);

    if (ctx && (ctx.state === 'suspended' || (ctx.state as any) === 'interrupted')) {
      ctx.resume().catch(() => {
        // Autoplay may restrict resume() until next user interaction.
        // The gesture unlock listeners will complete recovery on the very next touch.
      });
    }
  }

  public onBackground(_ctx: AudioContext | null): void {
    this.prewarmedThisGesture = false;
    // On iOS, WebKit CoreAudio pauses automatically upon backgrounding.
    // Crucially: DO NOT call ctx.suspend(), as that locks the context in a script-suspended state
    // that iOS Safari refuses to resume automatically without a direct user gesture.
  }

  public configureSession(isVoiceActive: boolean): void {
    this.isVoiceActive = isVoiceActive;
    // For iOS, configure session as 'ambient' so game sounds play without media player UI controls
    // and do not interrupt background audio.
    // Only configure session for game audio if WebRTC voice chat is NOT active
    // (voice chat manages its own 'play-and-record' audioSession).
    if (!isVoiceActive && typeof navigator !== 'undefined' && 'audioSession' in navigator) {
      try {
        (navigator as any).audioSession.type = 'ambient';
      } catch {}
    }
  }

  public dispose(): void {
    this.prewarmedThisGesture = false;
  }
}
